import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from '../../../store';
import { RootAction } from '../../../actions/ActionTypes';
import { fetchLocationThunk } from '../../../actions/location/LocationActions';
import { Location } from '../../../model/location';

export interface LocationDetailState {
    location: Location | null;
    loading: boolean;
    error: boolean;
    setLocation: (location: Location) => void;
}

/**
 * Location shown on the detail page. Uses the loaded locations list when it contains the id,
 * otherwise (direct link or refresh) fetches the location.
 */
export const useLocationDetail = (id: string): LocationDetailState => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, RootAction>>();
    const { locations } = useSelector((state: RootState) => state.locations);

    const storeLocation = useMemo(() => locations.find(item => item.id.toString() === id), [locations, id]);
    const isInStore = storeLocation !== undefined;

    const [fetchedLocation, setFetchedLocation] = useState<Location | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (isInStore) return;

        const locationId = Number(id);
        if (!Number.isInteger(locationId)) {
            setError(true);
            return;
        }

        let isCurrent = true;
        setFetchedLocation(null);
        setError(false);
        setLoading(true);

        dispatch(fetchLocationThunk(locationId))
            .then(location => {
                if (isCurrent) setFetchedLocation(location);
            })
            .catch(() => {
                if (isCurrent) setError(true);
            })
            .finally(() => {
                if (isCurrent) setLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [dispatch, id, isInStore]);

    return {
        location: storeLocation ?? fetchedLocation,
        loading: !isInStore && loading,
        error: !isInStore && error,
        setLocation: setFetchedLocation
    };
};

export default useLocationDetail;
